// src/components/Navbar.jsx
import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import Logo from "../assets/AllWellNewLogo2.png";
import {
  FaPhoneAlt,
  FaEnvelope,
  FaFacebookF,
  FaTwitter as FaXTwitter,
  FaLinkedinIn,
  FaGoogle,
  FaChevronDown,
  FaYoutube,
  FaInstagram,
  FaTiktok,
} from "react-icons/fa";

export default function Navbar() {
  const [open, setOpen] = useState(false); // mobile drawer
  const [scrolled, setScrolled] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false); // desktop Services dropdown
  const [mobileSvcOpen, setMobileSvcOpen] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const goToContact = () => {
    setOpen(false);
    navigate({ pathname: "/", hash: "#contact" });
  };

  const goHome = () => {
    setOpen(false);
    navigate("/", { replace: true });
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  const goToAbout = () => {
    setOpen(false);
    navigate({ pathname: "/", hash: "#about" });
  };

  // close services dropdown when clicking outside
  useEffect(() => {
    const onDocClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setSvcOpen(false);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkClass =
    "text-gray-800 hover:text-teal-700 transition font-medium text-base md:text-lg";

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-white/90 backdrop-blur border-b">
      {/* ===== Top utility bar ===== */}
      <div className="bg-[#0f2b4a] text-white">
        {/* Desktop / tablet */}
        <div className="hidden md:flex max-w-6xl mx-auto h-9 px-4 items-center justify-between text-sm">
          <div className="flex items-center gap-6">
            <a
              href="tel:16303399000"
              className="inline-flex items-center gap-2 text-white visited:text-white no-underline hover:text-emerald-300"
            >
              <FaPhoneAlt className="text-emerald-300" />
              630.339-9000
            </a>

            <a
              href="mailto:info@allwellcompanionhomecare.com"
              className="inline-flex items-center gap-2 text-white visited:text-white no-underline hover:text-emerald-300"
            >
              <FaEnvelope className="text-emerald-300" />
              info@allwellcompanionhomecare.com
            </a>
          </div>

          <div className="flex items-center gap-4">
            {/* You can pass either icon=... or children */}
            <Social
              icon={<FaFacebookF />}
              href="https://www.facebook.com/profile.php?id=61580686253934"
              label="Facebook"
            />
            <Social
              icon={<FaXTwitter />}
              href="https://twitter.com"
              label="X"
            />
            <Social
              icon={<FaLinkedinIn />}
              href="https://linkedin.com"
              label="LinkedIn"
            />
            <Social
              icon={<FaGoogle />}
              href="https://google.com"
              label="Google"
            />
            <Social
              icon={<FaYoutube />}
              href="https://youtube.com"
              label="YouTube"
            />
            <Social
              icon={<FaInstagram />}
              href="https://instagram.com"
              label="Instagram"
            />
            <Social
              icon={<FaTiktok />}
              href="https://tiktok.com"
              label="TikTok"
            />
          </div>
        </div>

        {/* Mobile compact bar */}
        {/* Mobile compact bar */}
        <div className="md:hidden max-w-6xl mx-auto h-10 px-2 flex items-center justify-between text-[11px]">
          {/* left: phone + email (both truncated to fit) */}
          <div className="flex items-center gap-2 overflow-hidden">
            <a
              href="tel:16303399000"
              aria-label="Call"
              className="inline-flex items-center gap-1 text-white visited:text-white overflow-hidden"
            >
              <FaPhoneAlt className="text-emerald-300" />
              <span className="whitespace-nowrap overflow-hidden text-ellipsis max-w-[110px]">
                630.339.9000
              </span>
            </a>

            <a
              href="mailto:info@allwellcompanionhomecare.com"
              aria-label="Email"
              className="inline-flex items-center gap-1 text-white visited:text-white overflow-hidden"
            >
              <FaEnvelope className="text-emerald-300" />
              <span className="whitespace-nowrap overflow-hidden text-ellipsis max-w-[150px] sm:max-w-[220px]">
                info@allwellcompanionhomecare.com
              </span>
            </a>
          </div>

          {/* right: social icons (small) — scroll if too many */}
          <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
            <Social href="https://www.facebook.com/profile.php?id=61580686253934" label="Facebook" small>
              <FaFacebookF />
            </Social>
            <Social href="https://twitter.com" label="X" small>
              <FaXTwitter />
            </Social>
            <Social href="https://linkedin.com" label="LinkedIn" small>
              <FaLinkedinIn />
            </Social>
            <Social href="https://youtube.com" label="YouTube" small>
              <FaYoutube />
            </Social>
            <Social href="https://instagram.com" label="Instagram" small>
              <FaInstagram />
            </Social>
            <Social href="https://tiktok.com" label="TikTok" small>
              <FaTiktok />
            </Social>
          </div>
        </div>
      </div>

      {/* ===== Main bar ===== */}
      <div className="max-w-6xl mx-auto h-[96px] md:h-[104px] px-6 flex items-center bg-white/90">
        {/* Logo */}
        <button
          onClick={goHome}
          className="flex items-center gap-3 mx-auto md:mx-0"
        >
          <img
            src={Logo}
            alt="AllWell Companion Home Care LLC"
            className={`transition-all duration-500 ${
              scrolled ? "w-[190px]" : "w-[300px]"
            }`}
          />
        </button>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8 mx-auto">
          <button type="button" onClick={goHome} className={linkClass}>
            Home
          </button>
          <button type="button" onClick={goToAbout} className={linkClass}>
            About Us
          </button>

          {/* Services dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              className={`${linkClass} inline-flex items-center gap-1`}
              aria-haspopup="menu"
              aria-expanded={svcOpen ? "true" : "false"}
              onClick={() => setSvcOpen((v) => !v)}
            >
              Services{" "}
              <FaChevronDown
                className={`transition ${svcOpen ? "rotate-180" : ""}`}
              />
            </button>

            <div
              role="menu"
              className={`absolute left-0 mt-2 w-56 rounded-md border bg-white shadow-lg py-2 z-50 ${
                svcOpen ? "" : "hidden"
              }`}
            >
              <NavLink
                to="/private-home-care"
                className="block px-4 py-2 hover:bg-gray-100 text-gray-800"
                onClick={() => setSvcOpen(false)}
              >
                Private Home Care
              </NavLink>
              <NavLink
                to="/Community-Care-Program"
                className="block px-4 py-2 hover:bg-gray-100 text-gray-800"
                onClick={() => setSvcOpen(false)}
              >
                Community Care Program (CCP)
              </NavLink>
              <NavLink
                to="/services"
                className="block px-4 py-2 hover:bg-gray-100 text-gray-800"
                onClick={() => setSvcOpen(false)}
              >
                Services Overview
              </NavLink>
            </div>
          </div>

          <NavLink to="/career" className={linkClass}>
            Career
          </NavLink>
        </nav>

        {/* Desktop CTA */}
        <div className="ml-auto hidden md:block">
          <button
            onClick={goToContact}
            className="rounded-lg bg-[#0f2b4a] text-white px-5 py-2 shadow hover:-translate-y-0.5 hover:shadow-md transition"
          >
            Contact Us
          </button>
        </div>

        {/* Hamburger (mobile) */}
        <button
          className="ml-auto md:hidden inline-flex items-center justify-center h-10 w-10 rounded-md border border-gray-200 shadow-sm"
          aria-label="Open menu"
          aria-expanded={open ? "true" : "false"}
          onClick={() => setOpen((v) => !v)}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path
              d={open ? "M6 6l12 12M18 6L6 18" : "M3 6h18M3 12h18M3 18h18"}
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      {/* ===== Mobile drawer ===== */}
      <div
        className={`md:hidden border-t bg-white ${open ? "block" : "hidden"}`}
      >
        <nav className="px-4 py-3 space-y-2">
          <button
            onClick={goHome}
            className="block w-full text-left rounded-md px-3 py-2 text-gray-800 hover:bg-gray-100"
          >
            Home
          </button>

          <button
            onClick={goToAbout}
            className="block w-full text-left rounded-md px-3 py-2 text-gray-800 hover:bg-gray-100"
          >
            About Us
          </button>

          {/* Mobile Services accordion */}
          <button
            className="w-full flex items-center justify-between rounded-md px-3 py-2 text-gray-800 hover:bg-gray-100"
            onClick={() => setMobileSvcOpen((v) => !v)}
            aria-expanded={mobileSvcOpen ? "true" : "false"}
          >
            <span>Services</span>
            <FaChevronDown
              className={`transition ${mobileSvcOpen ? "rotate-180" : ""}`}
            />
          </button>
          {mobileSvcOpen && (
            <div className="ml-3 space-y-1">
              <Item
                to="/private-home-care"
                onClick={() => {
                  setOpen(false);
                  setMobileSvcOpen(false);
                }}
              >
                • Private Home Care
              </Item>
              <Item
                to="/Community-Care-Program"
                onClick={() => {
                  setOpen(false);
                  setMobileSvcOpen(false);
                }}
              >
                • Community Care Program (CCP)
              </Item>
              <Item
                to="/services"
                onClick={() => {
                  setOpen(false);
                  setMobileSvcOpen(false);
                }}
              >
                • Services Overview
              </Item>
            </div>
          )}

          <Item to="/career" onClick={() => setOpen(false)}>
            Career
          </Item>

          <button
            onClick={goToContact}
            className="mt-2 w-full rounded-lg bg-[#0f2b4a] text-white py-2.5 font-semibold shadow hover:bg-[#123357] transition"
          >
            Contact Us
          </button>
        </nav>
      </div>
    </header>
  );
}

/* ---------- helpers ---------- */

function Item({ to, children, onClick }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className="block rounded-md px-3 py-2 text-gray-800 hover:bg-gray-100"
    >
      {children}
    </NavLink>
  );
}

/**
 * Social button that supports either:
 *   <Social icon={<FaFacebookF />} ... />
 * or
 *   <Social><FaFacebookF /></Social>
 * Always forces a visible size & color for the SVG.
 */
function Social({ href, label, children, icon, small }) {
  const el = icon || children;
  const size = small ? 14 : 16;

  return (
    <a
      href={href}
      aria-label={label}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center justify-center rounded-md transition
                  ${
                    small ? "h-7 w-7" : "h-8 w-8"
                  } bg-white/10 hover:bg-white/20`}
      style={{ lineHeight: 0 }}
    >
      {el ? React.cloneElement(el, { size, color: "#fff" }) : null}
    </a>
  );
}
